import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../../contexts/AuthContext'
import { usersApi, churchApi } from '../../services/api'
import toast from 'react-hot-toast'
import {
  UserIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  TrophyIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'

const profileSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  dateOfBirth: z.string().optional(),
  medicalConditions: z.string().optional(),
  dietaryRestrictions: z.string().optional(),
  emergencyContact: z.string().optional(),
  emergencyPhone: z.string().optional()
})

type ProfileForm = z.infer<typeof profileSchema>

interface Church {
  id: number
  name: string
  location: string
}

interface Competition {
  id: string
  name: string
  category: string
  type: 'individual' | 'team'
  status: 'registered' | 'pending' | 'approved'
}

const ProfileManagement: React.FC = () => {
  const { user } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [church, setChurch] = useState<Church | null>(null)
  const [competitions, setCompetitions] = useState<Competition[]>([])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      dateOfBirth: user?.dateOfBirth || '',
      medicalConditions: '',
      dietaryRestrictions: '',
      emergencyContact: '',
      emergencyPhone: ''
    }
  })

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        dateOfBirth: user.dateOfBirth || '',
        medicalConditions: '',
        dietaryRestrictions: '',
        emergencyContact: '',
        emergencyPhone: ''
      })
    }
  }, [user, reset])

  useEffect(() => {
    const fetchChurchInfo = async () => {
      if (user?.churchId) {
        try {
          const response = await churchApi.getChurches()
          const userChurch = response.data.find((c: Church) => c.id === user.churchId)
          setChurch(userChurch || null)
        } catch (error) {
          console.error('Failed to fetch church info:', error)
        }
      }
    }

    fetchChurchInfo()
  }, [user?.churchId])

  useEffect(() => {
    // In a real implementation, you would fetch the user's competition registrations
    // For now, we'll use mock data
    setCompetitions([
      {
        id: 'coptic_hymns',
        name: 'Coptic Hymns',
        category: 'Spiritual',
        type: 'individual',
        status: 'approved'
      },
      {
        id: 'chess',
        name: 'Chess',
        category: 'Sports',
        type: 'individual',
        status: 'pending'
      }
    ])
  }, [])

  const onSubmit = async (data: ProfileForm) => {
    setIsLoading(true)
    try {
      // Update user profile - using updateUser method
      if (user?.id) {
        await usersApi.updateUser(user.id, data)
        toast.success('Profile updated successfully!')
        setIsEditing(false)
        // In a real app, you'd want to refresh the user data here
      }
    } catch (error: any) {
      console.error('Failed to update profile:', error)
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    reset()
    setIsEditing(false)
  }

  const getAge = () => {
    if (!user?.dateOfBirth) return null
    const today = new Date()
    const birthDate = new Date(user.dateOfBirth)
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1
    }
    return age
  }

  const getCompetitionStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'registered':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getCompetitionTypeColor = (type: string) => {
    return type === 'team' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <UserIcon className="h-12 w-12 text-gray-400 mr-4" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {user?.firstName} {user?.lastName}
              </h1>
              <p className="text-gray-600">
                {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Profile
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {user?.status && (
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {user.status}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PencilSquareIcon className="h-4 w-4 mr-2" />
              Edit Profile
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSubmit(onSubmit)}
                disabled={isLoading || !isDirty}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              {isEditing ? (
                <input
                  {...register('firstName')}
                  type="text"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-900 py-2">{user?.firstName || 'Not provided'}</p>
              )}
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              {isEditing ? (
                <input
                  {...register('lastName')}
                  type="text"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-900 py-2">{user?.lastName || 'Not provided'}</p>
              )}
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <EnvelopeIcon className="h-4 w-4 inline mr-1" />
                Email Address
              </label>
              <p className="text-gray-900 py-2">{user?.email}</p>
              <p className="text-xs text-gray-500">Email cannot be changed. Contact your coordinator for changes.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <PhoneIcon className="h-4 w-4 inline mr-1" />
                Phone Number
              </label>
              {isEditing ? (
                <input
                  {...register('phone')}
                  type="tel"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="(555) 123-4567"
                />
              ) : (
                <p className="text-gray-900 py-2">{user?.phone || 'Not provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Date of Birth
              </label>
              {isEditing ? (
                <input
                  {...register('dateOfBirth')}
                  type="date"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <div>
                  <p className="text-gray-900 py-2">
                    {user?.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : 'Not provided'}
                  </p>
                  {getAge() && (
                    <p className="text-sm text-gray-500">Age: {getAge()} years</p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <BuildingOfficeIcon className="h-4 w-4 inline mr-1" />
                Church
              </label>
              <p className="text-gray-900 py-2">
                {church ? `${church.name} - ${church.location}` : 'Not assigned'}
              </p>
              <p className="text-xs text-gray-500">Church assignment managed by administrators.</p>
            </div>
          </div>

          {/* Medical & Emergency Information */}
          {isEditing && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Medical & Emergency Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medical Conditions (Optional)
                  </label>
                  <textarea
                    {...register('medicalConditions')}
                    rows={3}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="List any medical conditions, allergies, or medications..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dietary Restrictions (Optional)
                  </label>
                  <textarea
                    {...register('dietaryRestrictions')}
                    rows={3}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="List any dietary restrictions or food allergies..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emergency Contact Name
                  </label>
                  <input
                    {...register('emergencyContact')}
                    type="text"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Emergency contact person"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emergency Contact Phone
                  </label>
                  <input
                    {...register('emergencyPhone')}
                    type="tel"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Competition Registrations */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            <TrophyIcon className="h-5 w-5 inline mr-2" />
            Competition Registrations
          </h2>
        </div>

        {competitions.length > 0 ? (
          <div className="space-y-4">
            {competitions.map((competition) => (
              <div key={competition.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{competition.name}</h3>
                    <p className="text-sm text-gray-600">{competition.category}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCompetitionTypeColor(competition.type)}`}>
                      {competition.type}
                    </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCompetitionStatusColor(competition.status)}`}>
                      {competition.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <TrophyIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Competition Registrations</h3>
            <p className="text-gray-600">
              You haven't registered for any competitions yet. Contact your church coordinator to register.
            </p>
          </div>
        )}
      </div>

      {/* Important Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Important Notes</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Keep your contact information up to date for event notifications</li>
              <li>• Medical and emergency information is kept confidential and used only for safety purposes</li>
              <li>• Competition registrations must be approved by your church coordinator</li>
              <li>• Contact your church coordinator for any questions about registrations or events</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfileManagement